import { useEffect, useState } from "react";
import { Wallet } from "lucide-react";
import FinanceLayout from "@/components/FinanceLayout";
import MonthPicker from "@/components/MonthPicker";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api } from "@/lib/api";
import { fmtTL, EXPENSE_ITEMS, fmtDateTR } from "@/lib/finance";

export default function MonthlyExpenses() {
  const { workspace } = useWorkspace();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [item, setItem] = useState("");
  const [firm, setFirm] = useState("");
  const [firms, setFirms] = useState([]);
  const [data, setData] = useState({ recurring: [], onetime: [], recurring_total: 0, onetime_total: 0, grand_total: 0 });

  useEffect(() => { (async () => {
    const { data: fs } = await api.get("/finance/firms", { params: { workspace } });
    setFirms(fs);
  })(); }, [workspace]);

  useEffect(() => {
    (async () => {
      const params = { workspace, year, month };
      if (item) params.item = item;
      if (firm) params.firm = firm;
      const { data: d } = await api.get("/finance/monthly-expenses", { params });
      setData(d);
    })();
  }, [workspace, year, month, item, firm]);

  return (
    <FinanceLayout>
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Aylık Gider Tablosu</h1>
          <p className="text-sm text-slate-500 mt-2">Seçili aya ait tüm giderleri görüntüleyin ve raporlayın.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} testId="me-monthpicker" />
          <select value={item} onChange={(e) => setItem(e.target.value)} data-testid="me-item-filter" className="h-11 rounded-lg border border-slate-200 bg-white px-4 text-sm">
            <option value="">Gider Kalemi: Tümü</option>
            {EXPENSE_ITEMS.map((it) => <option key={it} value={it}>{it}</option>)}
          </select>
          <select value={firm} onChange={(e) => setFirm(e.target.value)} data-testid="me-firm-filter" className="h-11 rounded-lg border border-slate-200 bg-white px-4 text-sm">
            <option value="">Firma: Tümü</option>
            {firms.map((fb) => <option key={fb} value={fb}>{fb}</option>)}
            <option value="isletme">İşletme Gideri</option>
          </select>
          <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-5 py-3 flex items-center gap-3">
            <Wallet size={18} className="text-emerald-700" />
            <div>
              <div className="text-xs text-slate-500">Toplam Gider Tutarı</div>
              <div className="text-xl font-bold text-emerald-800" data-testid="me-total">{fmtTL(data.grand_total)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Recurring */}
      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-slate-100 font-semibold text-slate-900">Sabit Giderler</div>
        {data.recurring.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="me-rec-empty">Sabit gider yok.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/40">
                <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Firma</th>
                <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Gider Adı</th>
                <th className="text-right py-3 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Tutar</th>
              </tr>
            </thead>
            <tbody>
              {data.recurring.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 last:border-none">
                  <td className="py-3 px-6 text-sm text-slate-700">{r.firm === "isletme" || r.is_business ? "İşletme Gideri" : r.firm}</td>
                  <td className="py-3 px-6 text-sm text-slate-800">{r.name}</td>
                  <td className="py-3 px-6 text-sm font-semibold text-slate-900 text-right">{fmtTL(r.amount)}</td>
                </tr>
              ))}
              <tr className="bg-slate-50/40">
                <td colSpan={2} className="py-3 px-6 text-sm font-semibold text-slate-500">Toplam Sabit Gider</td>
                <td className="py-3 px-6 text-sm font-bold text-emerald-800 text-right" data-testid="me-rec-total">{fmtTL(data.recurring_total)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* One-time */}
      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 font-semibold text-slate-900">Tek Seferlik Giderler</div>
        {data.onetime.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="me-ot-empty">Bu ay tek seferlik gider yok.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/40">
                <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Firma</th>
                <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Gider Kalemi</th>
                <th className="text-right py-3 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Tutar</th>
                <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Tarih</th>
              </tr>
            </thead>
            <tbody>
              {data.onetime.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 last:border-none">
                  <td className="py-3 px-6 text-sm text-slate-700">{r.firm || "—"}</td>
                  <td className="py-3 px-6 text-sm text-slate-800">{r.item || "—"}</td>
                  <td className="py-3 px-6 text-sm font-semibold text-slate-900 text-right">{fmtTL(r.amount)}</td>
                  <td className="py-3 px-6 text-sm text-slate-600">{fmtDateTR(r.date)}</td>
                </tr>
              ))}
              <tr className="bg-slate-50/40">
                <td colSpan={2} className="py-3 px-6 text-sm font-semibold text-slate-500">Toplam Tek Seferlik Gider</td>
                <td className="py-3 px-6 text-sm font-bold text-emerald-800 text-right" data-testid="me-ot-total">{fmtTL(data.onetime_total)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </FinanceLayout>
  );
}
